import React from 'react';
import { ROUTES, RouteType } from '../types/navigationTypes';
import { buildPath } from './navigationUtils';
import { SupportedOperation } from '@amzn/snow-data-service-typescript-client';

export interface BreadcrumbItemData {
	text: string;
	href: string;
	onClick: (e: React.MouseEvent) => void;
}

export interface BreadcrumbContext {
	currentView: RouteType;
	selectedApplicationId: string | null;
	selectedApplicationName: string | null;
	selectedOperationId: string | null;
	selectedOperationName: string | null;
	selectedSupportedOperation: SupportedOperation | undefined;
}

export interface BreadcrumbActions {
	navigateToApplications: () => void;
	navigateToApplicationDetails: (applicationId: string, applicationName?: string) => void;
	navigateToOperationDetails: (operationId: string, operationName?: string, operation?: SupportedOperation) => void;
}

export const createBaseBreadcrumbItem = (actions: BreadcrumbActions): BreadcrumbItemData => ({
	text: 'Applications',
	href: buildPath(),
	onClick: (e: React.MouseEvent) => {
		e.preventDefault();
		actions.navigateToApplications();
	},
});

export const createApplicationBreadcrumbItem = (
	context: BreadcrumbContext,
	actions: BreadcrumbActions
): BreadcrumbItemData => ({
	text: context.selectedApplicationName || context.selectedApplicationId!,
	href: buildPath([context.selectedApplicationId!]),
	onClick: (e: React.MouseEvent) => {
		e.preventDefault();
		actions.navigateToApplicationDetails(
			context.selectedApplicationId!,
			context.selectedApplicationName || undefined
		);
	},
});

export const createOperationBreadcrumbItem = (
	context: BreadcrumbContext,
	actions: BreadcrumbActions
): BreadcrumbItemData => ({
	text: context.selectedOperationName || context.selectedOperationId!,
	href: buildPath([context.selectedApplicationId!, context.selectedOperationId!]),
	onClick: (e: React.MouseEvent) => {
		e.preventDefault();
		if (context.selectedSupportedOperation) {
			actions.navigateToOperationDetails(
				context.selectedOperationId!,
				context.selectedOperationName || undefined,
				context.selectedSupportedOperation
			);
		} else {
			actions.navigateToApplicationDetails(
				context.selectedApplicationId!,
				context.selectedApplicationName || undefined
			);
		}
	},
});

export const createAddServiceBreadcrumbItem = (): BreadcrumbItemData => ({
	text: 'Add Service',
	href: buildPath(['add']),
	onClick: (e: React.MouseEvent) => e.preventDefault(),
});

export const createAddOperationBreadcrumbItem = (applicationId: string): BreadcrumbItemData => ({
	text: 'Add Operation',
	href: buildPath([applicationId, 'add']),
	onClick: (e: React.MouseEvent) => e.preventDefault(),
});

export const createExportToTaskBreadcrumbItem = (
	applicationId: string,
	operationId: string
): BreadcrumbItemData => ({
	text: 'Export to Task',
	href: buildPath([applicationId, operationId, 'export']),
	onClick: (e: React.MouseEvent) => e.preventDefault(),
});

export const needsApplicationContext = (currentView: RouteType): boolean => {
	return (
		currentView === ROUTES.APPLICATION_DETAILS ||
		currentView === ROUTES.ADD_OPERATION ||
		currentView === ROUTES.OPERATION_DETAILS ||
		currentView === ROUTES.EXPORT_OPERATION_TO_TASK
	);
};

export const needsOperationContext = (currentView: RouteType): boolean => {
	return (
		currentView === ROUTES.OPERATION_DETAILS ||
		currentView === ROUTES.EXPORT_OPERATION_TO_TASK
	);
};